import { max, min, timeDay, timeFormat, timeParse } from "d3";
import { formatNumber } from "@/_core/_helpers/_parametric_tools";

// Month with 3 letters used in the contracts (contract_month_year).
export const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

// Format month-year of a contract: 3 letters of month + 2 digits of year
// (e.g. "MAR27"). The regex is derived from MONTHS to avoid repeating the list.
export const MONTH_YEAR_REGEX = new RegExp(`^(${MONTHS.join("|")})\\d{2}$`);

// Validates a month-year code, normalizing to uppercase (accepts "mar27").
export function isMonthYearCode(value) {
  return value != null && MONTH_YEAR_REGEX.test(String(value).toUpperCase());
}

export function capitalizeFirst(str) {
  if (typeof str !== "string" || !str.length) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function exportToCSV(data, fileName = "data.csv") {
  // Get headers (keys of the first object)
  const headers = Object.keys(data[0]);

  // Create rows: headers + data
  const csvRows = [
    headers.join(","), // Header row
    ...data.map((obj) => headers.map((header) => `"${obj[header]}"`).join(",")), // Data rows
  ];

  // Join all rows with line breaks
  const csvContent = csvRows.join("\n");

  // Create a link to download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.click();
  URL.revokeObjectURL(url); // Release the URL object
}

export function commodityTooltip(d) {
  return (
    "<table class='table table-borderless table-sm text-left table-analytics'>\n" +
    "  <thead>\n" +
    "    <tr style='border-bottom: 1px solid var(--lightbackground);" +
    "  background: var(--lightbackground-alpha)'>\n" +
    "      <th colspan='2'>COMMODITY INFORMATION</th>\n" +
    "    </tr>\n" +
    "  </thead>\n" +
    "  <tbody>\n" +
    "    <tr>\n" +
    "      <th>NAME</th>\n" +
    "      <td>" +
    d.commodity_name +
    "   </td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <th>TYPE</th>\n" +
    "      <td>" +
    d.fk_idcommoditytype +
    "   </td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <th>SOURCE</th>\n" +
    "      <td>" +
    d.fk_idcommoditysource +
    "   </td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <th>CONTRACT SIZE</th>\n" +
    "      <td>" +
    formatNumber(d.contract_size) +
    " " +
    d.fk_idunit +
    "   </td>\n" +
    "    </tr>\n" +
    "    <tr>\n" +
    "      <th>DEFAULT CONTRACT</th>\n" +
    "      <td>" +
    d.selected_contract +
    "   </td>\n" +
    "    </tr>\n" +
    "    <tr style='border-bottom: 1px solid var(--lightbackground)'>\n" +
    "      <th>QUOTATION</th>\n" +
    "      <td>" +
    d.fk_idcurrency +
    "/" +
    d.fk_idunit +
    "   </td>\n" +
    "    </tr>\n" +
    "  </tbody>\n" +
    "</table>"
  );
}
export function getUniqueValues(array) {
  return [
    ...new Set(
      array.map((item) => (item instanceof Date ? item.getTime() : item)),
    ),
  ].map((val) =>
    typeof val === "number" &&
    array.some((item) => item instanceof Date && item.getTime() === val)
      ? new Date(val)
      : val,
  );
}
export function add(accumulator, a) {
  return accumulator + a;
}
export function sortByKey(array, key) {
  return array.sort(function(a, b) {
    var x = a[key];
    var y = b[key];
    return x < y ? -1 : x > y ? 1 : 0;
  });
}
export function sortByKeyDesc(array, key) {
  return array.sort(function(a, b) {
    var x = a[key];
    var y = b[key];
    return x < y ? 1 : x > y ? -1 : 0;
  });
}
export function sortByColumns(data, columns) {
  return data.sort((a, b) => {
    for (let { key, order } of columns) {
      let valA = a[key];
      let valB = b[key];

      // Handle null/undefined values
      if (valA == null && valB == null) continue;
      if (valA == null) return order === "asc" ? -1 : 1;
      if (valB == null) return order === "asc" ? 1 : -1;

      // Check if values are dates
      const isDateA = valA instanceof Date;
      const isDateB = valB instanceof Date;

      if (isDateA && isDateB) {
        valA = valA.getTime();
        valB = valB.getTime();
      }
      // If values are numeric, compare as numbers
      else if (!isNaN(valA) && !isNaN(valB)) {
        valA = parseFloat(valA);
        valB = parseFloat(valB);
      }
      // Otherwise, compare as strings (case-insensitive)
      else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return order === "asc" ? -1 : 1;
      if (valA > valB) return order === "asc" ? 1 : -1;
    }
    return 0; // If all compared columns are equal
  });
}
export function rankArray(arr) {
  let nulls = 0;
  arr.forEach(function(d) {
    if (d === null) {
      nulls += 1;
    }
  });
  if (nulls === arr.length) {
    return arr;
  }
  // https://stackoverflow.com/questions/14834571/ranking-array-elements
  const sorted = arr.slice().sort(function(a, b) {
    return b - a;
  });
  let rank = arr.map(function(v) {
    return sorted.indexOf(v) + 1;
  });
  arr.forEach(function(d, i) {
    if (d === null) {
      rank[i] = null;
    }
  });
  return rank;
}

/**
 * Compares two objects based on specified headers and checks if any of the values are different.
 *
 * @param {Object} objA - First object to compare.
 * @param {Object} objB - Second object to compare.
 * @param {Array} headers - List of keys (headers) to compare values from both objects.
 * @returns {boolean} - Returns true if at least one header has a different value between the two objects, otherwise false.
 */
export function compareRowsByHeader(objA, objB, headers) {
  return headers.some(function(header) {
    let valA = objA[header];
    let valB = objB[header];
    // Check if values are dates
    const isDateA = valA instanceof Date;
    const isDateB = valB instanceof Date;
    if (isDateA && isDateB) {
      valA = valA.getTime();
      valB = valB.getTime();
    }
    // If values are numeric, compare as numbers
    else if (!isNaN(valA) && !isNaN(valB)) {
      valA = parseFloat(valA);
      valB = parseFloat(valB);
    }
    // Otherwise, compare as strings (case-insensitive)
    else {
      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();
    }
    return valA !== valB;
  });
}

//
export function sortByKey_Macro(array, key) {
  return array.sort(function(a, b) {
    var x = isFinite(a[key]) ? a[key] : 1000000;
    var y = isFinite(b[key]) ? b[key] : 1000000;
    return x < y ? -1 : x > y ? 1 : 0;
  });
}
export function formatDate_MDYHMS(date) {
  const parseDate = timeFormat("%m/%d/%Y %I:%M:%S"); // 12h format
  return parseDate(new Date(date));
}
export function formatDate_YMD_AddDays(date, days) {
  let parseDate = timeParse("%Y-%m-%d");
  let formatDate = timeFormat("%Y-%m-%d");

  let dateChanged = parseDate(date); // Convert string to Date object
  if (!dateChanged) return null; // Handle invalid date case

  let newDate = timeDay.offset(dateChanged, days); // Add days
  return formatDate(newDate); // Convert back to string
}
export function formatDate_DMY_AddDays(date, days) {
  let parseDate = timeParse("%d-%m-%Y"); // Parse string to Date object
  let formatDate = timeFormat("%d-%m-%Y"); // Format Date object to string

  let dateChanged = parseDate(date);
  if (!dateChanged) return null; // Handle invalid date case

  dateChanged.setDate(dateChanged.getDate() + days); // Add days
  return formatDate(dateChanged); // Convert back to string
}
export function formatDate_YMD(date) {
  return timeFormat("%Y-%m-%d")(new Date(date));
}
export function formatDate_YMDHMS(date) {
  return timeFormat("%Y-%m-%d, %I:%M:%S")(new Date(date)); // 12h format
}
export function formatDate_DMY(date) {
  return timeFormat("%d-%m-%Y")(new Date(date));
}
export function formatDate_YMDSlash(date) {
  return timeFormat("%m/%d/%Y")(new Date(date));
}
export function formatDate_DDMMMYHMS(date) {
  return timeFormat("%d %b %Y, %I:%M:%S")(new Date(date));
}

export function finitNum(num, format) {
  return isFinite(num) && num !== 0 ? format(num) : "-";
}
export function roundToFloorMin(date = new Date()) {
  const minutes = 30;
  const ms = 1000 * 60 * minutes;
  return new Date(Math.floor(date.getTime() / ms) * ms);
}
export function definedFct(dat) {
  return dat === 0 ? 1 : dat;
}
export function roundDateToFloor(date = new Date(), minutes = 60) {
  let rightNow = new Date();
  let previous = new Date(date.getTime());
  let dif = (rightNow.getMinutes() - previous.getMinutes()) * 60000;
  previous.setTime(previous.getTime() + dif);
  previous.setDate(date.getDate() - 1);
  const ms = 1000 * 60 * minutes;
  return new Date(Math.floor(previous.getTime() / ms) * ms);
}
export function noNanNum(num, defaultVal = 0) {
  return isFinite(num) && num !== null && !isNaN(num) ? num : defaultVal;
}
export function lengthByDate(pickedTimeframe, dates) {
  let nLen = dates.length - 1;
  if (nLen > -1) {
    let searchedDate = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
      JSON.parse(JSON.stringify(dates[nLen])),
    );
    searchedDate.setHours(0);
    if (pickedTimeframe === "1D") {
      return 1;
    } else if (pickedTimeframe === "1M") {
      searchedDate.setMonth(searchedDate.getMonth() - 1);
    } else if (pickedTimeframe === "1W") {
      searchedDate.setDate(searchedDate.getDate() - 7);
    } else if (pickedTimeframe === "1Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 1);
    } else if (pickedTimeframe === "2D") {
      return 2;
    } else if (pickedTimeframe === "2M") {
      searchedDate.setMonth(searchedDate.getMonth() - 2);
    } else if (pickedTimeframe === "2W") {
      searchedDate.setDate(searchedDate.getDate() - 14);
    } else if (pickedTimeframe === "2Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 2);
    } else if (pickedTimeframe === "20Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 20);
    } else if (pickedTimeframe === "3D") {
      return 3;
    } else if (pickedTimeframe === "3M") {
      searchedDate.setMonth(searchedDate.getMonth() - 3);
    } else if (pickedTimeframe === "3Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 3);
    } else if (pickedTimeframe === "4D") {
      return 4;
    } else if (pickedTimeframe === "4Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 4);
    } else if (pickedTimeframe === "5Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 5);
    } else if (pickedTimeframe === "6M") {
      searchedDate.setMonth(searchedDate.getMonth() - 6);
    } else if (pickedTimeframe === "9M") {
      searchedDate.setMonth(searchedDate.getMonth() - 9);
    } else if (pickedTimeframe === "10Y") {
      searchedDate.setFullYear(searchedDate.getFullYear() - 10);
    } else if (pickedTimeframe === "18M") {
      searchedDate.setMonth(searchedDate.getMonth() - 18);
    }
    let i = 0;
    while (i <= nLen) {
      i++;
      if (
        dates[nLen - i] <= searchedDate &&
        dates[nLen - i + 1] > searchedDate
      ) {
        if (dates[nLen - i] < searchedDate) {
          i--;
        }
        break;
      }
    }
    // i++;
    return Math.min(i, nLen);
  } else {
    return -1;
  }
}
export function lengthByDateForward(pickedTimeframe, dates) {
  let nLen = dates.length - 1;
  let searchedDate = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
    JSON.parse(JSON.stringify(dates[0])),
  );
  searchedDate.setHours(0);

  if (pickedTimeframe === "1D") {
    return 1;
  } else if (pickedTimeframe === "1M") {
    searchedDate.setMonth(searchedDate.getMonth() + 1);
  } else if (pickedTimeframe === "1W") {
    searchedDate.setDate(searchedDate.getDate() + 7);
  } else if (pickedTimeframe === "1Y") {
    searchedDate.setFullYear(searchedDate.getFullYear() + 1);
  } else if (pickedTimeframe === "2D") {
    return 2;
  } else if (pickedTimeframe === "2M") {
    searchedDate.setMonth(searchedDate.getMonth() + 2);
  } else if (pickedTimeframe === "2W") {
    searchedDate.setDate(searchedDate.getDate() + 14);
  } else if (pickedTimeframe === "2Y") {
    searchedDate.setFullYear(searchedDate.getFullYear() + 2);
  } else if (pickedTimeframe === "3D") {
    return 3;
  } else if (pickedTimeframe === "3M") {
    searchedDate.setMonth(searchedDate.getMonth() + 3);
  } else if (pickedTimeframe === "3Y") {
    searchedDate.setFullYear(searchedDate.getFullYear() + 3);
  } else if (pickedTimeframe === "4D") {
    return 4;
  } else if (pickedTimeframe === "4Y") {
    searchedDate.setFullYear(searchedDate.getFullYear() + 4);
  } else if (pickedTimeframe === "5Y") {
    searchedDate.setFullYear(searchedDate.getFullYear() + 5);
  } else if (pickedTimeframe === "6M") {
    searchedDate.setMonth(searchedDate.getMonth() + 6);
  } else if (pickedTimeframe === "9M") {
    searchedDate.setMonth(searchedDate.getMonth() + 9);
  } else if (pickedTimeframe === "10Y") {
    searchedDate.setFullYear(searchedDate.getFullYear() + 10);
  } else if (pickedTimeframe === "18M") {
    searchedDate.setMonth(searchedDate.getMonth() + 18);
  }
  let i = 0;
  while (i <= nLen) {
    i++;
    if (dates[i] <= searchedDate && dates[i + 1] > searchedDate) {
      break;
    }
  }
  return i;
}

export function date2Str(newDate, type = 2) {
  let mnth = newDate.getMonth() + 1;
  let cero1 = "";
  if (mnth < 10) {
    cero1 = "0";
  }
  let cero2 = "";
  if (newDate.getDate() < 10) {
    cero2 = "0";
  }
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dev",
  ];
  return type === 0
    ? newDate.getFullYear() +
        "-" +
        cero1 +
        "" +
        mnth +
        "-" +
        cero2 +
        "" +
        newDate.getDate()
    : type === 1
    ? cero1 +
      "" +
      mnth +
      "/" +
      cero2 +
      "" +
      newDate.getDate() +
      "/" +
      newDate.getFullYear()
    : cero2 +
      "" +
      newDate.getDate() +
      " " +
      monthNames[mnth] +
      " " +
      (newDate.getFullYear() - 2000);
}

export function additionalYSpace(minMaxYAxis, del, type = "both") {
  del = del * (minMaxYAxis[1] - minMaxYAxis[0]);
  if (type == "both" || type == "top") minMaxYAxis[1] = minMaxYAxis[1] + del;
  if (type == "both" || type == "bottom") minMaxYAxis[0] = minMaxYAxis[0] - del;
  return minMaxYAxis;
}

export function indexOfSmallest(data) {
  return data.reduce(function(lowest, next, index) {
    return next < data[lowest] ? index : lowest;
  }, 0);
}

export function longShortTransform(val) {
  let res = val;
  if (res.toUpperCase() === "BUY") {
    res = "LONG";
  } else if (res.toUpperCase() === "SELL") {
    res = "SHORT";
  } else if (
    res.toUpperCase() === "OFFSET_A_LONG_POSITION" ||
    res.toUpperCase() === "OFFSET A LONG POSITION"
  ) {
    res = "OFFSET LONG";
  } else if (
    res.toUpperCase() === "OFFSET_A_SHORT_POSITION" ||
    res.toUpperCase() === "OFFSET A SHORT POSITION"
  ) {
    res = "OFFSET SHORT";
  }
  return res;
}

export function noLiquidity(data) {
  let line = 0;
  data.forEach(function(d) {
    line = line + (d.close !== d.open ? 1 : 0);
  });
  return line / data.length > 0.5;
}

export function ema(data, r) {
  // https://medium.com/codex/calculating-the-exponential-moving-average-in-javascript-84dfea8d55cc
  // https://stackoverflow.com/questions/40057020/calculating-exponential-moving-average-ema-using-javascript
  // Linear loop: the previous reduce + concat copied the array on every step (O(n²)).
  const result = new Array(data.length);
  if (data.length === 0) {
    return result;
  }
  result[0] = data[0];
  for (let i = 1; i < data.length; i++) {
    result[i] = (2 * data[i]) / (r + 1) + (result[i - 1] * (r - 1)) / (r + 1);
  }
  return result;
}

export function rollingMaxMin(data, n, field1 = "low", field2 = "high") {
  let minMax = [];
  let sliced;
  data.forEach(function(d, i) {
    sliced = data.slice(Math.max(0, i - n + 1), i + 1);
    minMax.push({
      min: min(sliced, (d) => d[field1]),
      max: max(sliced, (d) => d[field2]),
    });
  });
  return minMax;
}

export function dateRangeToString(
  date1,
  date2,
  includeDate2 = true,
  divider = "-",
) {
  let date1Copy = timeDay(
    timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date1))),
  );
  let date2Copy = timeDay(
    timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date2))),
  );
  if (!includeDate2) {
    date2Copy = new Date(date2Copy.setDate(date2Copy.getDate() - 1));
  }
  let str;
  if (date1Copy.getFullYear() !== date2Copy.getFullYear()) {
    str =
      timeFormat("%b %d, %y")(date1Copy) +
      divider +
      timeFormat("%b %d, %y")(date2Copy);
  } else if (date1Copy.getMonth() !== date2Copy.getMonth()) {
    str =
      timeFormat("%b %d")(date1Copy) +
      divider +
      timeFormat("%b %d, %y")(date2Copy);
  } else {
    str =
      timeFormat("%b %d")(date1Copy) +
      divider +
      timeFormat("%d, %y")(date2Copy);
  }
  return str;
}

export function timeParseNoNull(dat) {
  let newDat = timeParse("%Y-%m-%d")(dat);
  return newDat === null ? new Date(1960, 0, 1) : newDat;
}

export function timeParseNoNull2(dat) {
  let parsed = timeParseNull2(dat);
  return parsed === null ? new Date(1960, 0, 1) : parsed;
}

export function timeParseNull2(dat) {
  // Handle null, undefined, empty input, or "None"
  if (dat == null || dat === "" || dat === "None") return null;

  // Handle numeric timestamp (e.g., 1748020046575)
  if (!isNaN(dat) && dat.toString().length >= 12) {
    const timestamp = parseInt(dat, 10);
    return new Date(timestamp);
  }

  // Normalize timezone offset if present
  if (typeof dat === "string") {
    dat = dat.replace(/[+-]\d{2}:\d{2}$/, ""); // remove timezone offset
  }

  // D3.js timeParse for Django-like formats
  const parsers = [
    timeParse("%Y-%m-%d %H:%M:%S.%f"), // Django with microseconds (e.g., 2015-05-01 00:00:00.000000)
    timeParse("%Y-%m-%dT%H:%M:%S.%fZ"), // ISO 8601 with microseconds and Z (e.g., 2025-03-12T14:08:21.212642Z)
    timeParse("%Y-%m-%dT%H:%M:%S.%f"), // ISO 8601 with microseconds (e.g., 2024-12-05T23:50:08.934558)
    timeParse("%Y-%m-%dT%H:%M:%S.%LZ"), // ISO 8601 with milliseconds and Z (e.g., 2024-11-07T00:00:00.000Z)
    timeParse("%Y-%m-%dT%H:%M:%S.%L"), // ISO 8601 with milliseconds (e.g., 2024-11-07T00:00:00.000)
    timeParse("%Y-%m-%d %H:%M:%S"), // Django default with time (e.g., 2024-11-07 00:00:00)
    timeParse("%Y-%m-%d"), // Date only (e.g., 2025-04-22)
  ];

  // Try each parser until one succeeds
  for (const parser of parsers) {
    const parsed = parser(dat);
    if (parsed) return parsed;
  }

  // Log invalid dates for debugging (optional)
  console.warn(`Unable to parse date: ${dat}`);
  return null;
}

export function contractMonthYearParse(dat) {
  if (dat instanceof Date) {
    return dat;
  }
  if (dat === "" || dat == null) {
    return new Date(1960, 0, 1);
  }
  return timeParse("%b%y")(dat);
}

export function prepDate(dat) {
  let newDat;
  const parseDate = timeParse("%Y-%m-%dT%H:%M:%S.%LZ");
  dat = parseDate(dat);

  if (!dat || +dat < +new Date(1980, 1, 1)) {
    newDat = "";
  } else {
    const formatDate = timeFormat("%Y-%m-%d");
    newDat = formatDate(dat);
  }
  return newDat;
}

export function replaceCharacters(str) {
  return str.replace(
    /(\+)|(-)|(\d+)|([.,()\\"/&])/g,
    (match, plus, minus, number, special) => {
      if (plus) return "long";
      if (minus) return "buy";
      if (number) return "Num";
      if (special) return "special";
    },
  );
}
/**
 * Today with 0 hours, min, sec, ms
 */
export function todayWithoutTime() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

export function assetSelectionVuex(_this, asset) {
  _this.$store.dispatch("user_preferences/updateSelectedNav", asset);
}

export function colorThemeVuex(_this, clrTheme) {
  _this.$store.dispatch("user_preferences/updateColorTheme", clrTheme);
}

export function getSkeletonTable() {
  return [
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
    { date: 1, price1: 1 },
  ];
}

export function colorThemeChange(root, colorTheme) {
  if (colorTheme) {
    root.style.setProperty("--symmetry-h", "rgb(38, 123, 255)");
    root.style.setProperty("--symmetry", "rgb(10, 100, 242)");
    root.style.setProperty("--symmetry-alpha", "rgba(10, 100, 242, 0.60)");
    root.style.setProperty("--symmetry-alpha-2", "rgba(10, 100, 242, 0.75)");
    root.style.setProperty("--logo-blue", "rgb(8, 114, 242)");
    root.style.setProperty("--expo", "rgb(19, 69, 242)");
    root.style.setProperty("--expo-alpha", "rgba(19, 69, 242, 0.60)");
    root.style.setProperty("--expo-alpha-2", "rgba(19, 69, 242, 0.1)");
    root.style.setProperty("--expo-alpha-3", "rgba(19, 69, 242, 0.20)");
    root.style.setProperty("--expo-alpha-4", "rgba(19, 69, 242, 0.75)");
    root.style.setProperty("--dark-expo", "rgb(15, 56, 198)");
    root.style.setProperty("--dark-expo-2", "rgb(29, 52, 139)");
    root.style.setProperty("--dark-expo-alpha", "rgba(15, 56, 198, 0.60)");
    root.style.setProperty("--expo-navbar", "rgba(15, 56, 198, 0.60)");
    root.style.setProperty("--hedge-h", "rgb(68, 193, 255)");
    root.style.setProperty("--hedge", "rgb(26, 159, 222)");
    root.style.setProperty("--dark-hedge", "rgb(19, 123, 171)");
    root.style.setProperty("--hedge-alpha", "rgba(26, 159, 222, 0.60)");
    root.style.setProperty("--hedge-alpha2", "rgba(29, 174, 244, 0.85)");
    root.style.setProperty("--hedge-alpha-3", "rgba(29, 174, 244, 0.2)");
    root.style.setProperty("--dark-hedge-alpha", "rgba(19, 123, 171, 0.60)");
    root.style.setProperty("--hedge-navbar", "rgba(19, 123, 171, 0.60)");
    root.style.setProperty("--net", "rgb(255, 224, 0)");
    root.style.setProperty("--net-alpha", "rgba(255, 224, 0, 0.60)");
    root.style.setProperty("--net_2", "rgb(255, 121, 0)");
    root.style.setProperty("--net-alpha_2", "rgba(255, 121, 0, 0.6)");
    root.style.setProperty("--net-alpha-2", "rgba(255, 224, 0, 0.1)");
    root.style.setProperty("--program", "rgb(112, 239, 242)");
    root.style.setProperty("--program-alpha", "rgba(112, 239, 242, 0.2)");
    root.style.setProperty("--fwd-h", "rgb(10, 231, 161)");
    root.style.setProperty("--fwd", "rgb(9, 204, 143)");
    root.style.setProperty("--fwd-alpha", "rgba(9, 204, 143, 0.60)");
    root.style.setProperty("--fwd-alpha-2", "rgba(9, 204, 143, 0.20)");
    root.style.setProperty("--dark-fwd", "rgb(11, 150, 96)");
    root.style.setProperty("--dark-fwd-alpha", "rgba(11, 150, 96, 0.60)");
    root.style.setProperty("--dark-fwd-alpha-2", "rgba(11, 150, 96, 0.20)");
    root.style.setProperty("--call", "rgb(249, 49, 159)");
    root.style.setProperty("--call-alpha", "rgba(249, 49, 142, 0.60)");
    root.style.setProperty("--call-alpha-2", "rgba(249, 49, 142, 0.20)");
    root.style.setProperty("--dark-call", "rgb(224, 12, 59)");
    root.style.setProperty("--dark-call-alpha", "rgba(224, 12, 59, 0.60)");
    root.style.setProperty("--dark-call-alpha-2", "rgba(224, 12, 59, 0.20)");
    root.style.setProperty("--put", "rgb(105, 105, 255)");
    root.style.setProperty("--put-alpha", "rgba(105, 105, 255, 0.2)");
    root.style.setProperty("--dark-put", "rgb(74, 63, 229)");
    root.style.setProperty("--dark-put-alpha", "rgba(74, 63, 229, 0.2)");
    root.style.setProperty("--pink", "rgb(249, 49, 197)");
    root.style.setProperty("--dark-pink", "rgb(232, 35, 113)");
    root.style.setProperty("--red", "rgb(255, 0, 59)");
    root.style.setProperty("--red-alpha", "rgba(255, 0, 59, 0.60)");
    root.style.setProperty("--red-alpha-2", "rgba(255, 0, 59, 0.10)");
    root.style.setProperty("--red-alpha-3", "rgba(255, 0, 59, 0.20)");
    root.style.setProperty("--dark-red", "rgb(171, 1, 34)");
    root.style.setProperty("--dark-red-alpha", "rgba(171, 1, 34, 0.60)");
    root.style.setProperty("--darker-red", "rgb(122, 0, 17)");
    root.style.setProperty("--green", "rgb(9, 204, 143)");
    root.style.setProperty("--dark-green", "rgb(11, 150, 96)");
    root.style.setProperty("--yellow-alpha", "rgba(255, 224, 0, 0.60)");
    root.style.setProperty("--white", "rgba(255, 255, 255, 1)");
    root.style.setProperty("--whiter-gray", "rgb(247, 248, 250)");
    root.style.setProperty("--white-gray", "rgba(160, 165, 170, 1)");
    root.style.setProperty("--gray", "rgba(108, 115, 122, 1)");
    root.style.setProperty("--transparent", "rgba(0, 0, 0, 0)");
    root.style.setProperty("--black-gray", "rgba(90, 95, 100, 1)");
    root.style.setProperty("--blacker-gray", "rgba(35, 38, 42, 1)");
    root.style.setProperty("--blacker-gray-fixed", "rgba(35, 38, 42, 1)");
    root.style.setProperty("--black", "rgba(0, 0, 0, 1)");
    root.style.setProperty("--whiteGray", "rgb(160, 165, 170)");
    root.style.setProperty("--whiteGray-alpha", "rgba(160, 165, 170, 0.60)");
    root.style.setProperty(
      "--graylightbackgroundFixed",
      "rgba(108, 115, 122, 1)",
    );
    root.style.setProperty("--graylightbackground", "rgba(108, 115, 122, 1)");
    root.style.setProperty(
      "--graylightbackground-alpha",
      "rgba(108, 115, 122, 0.5)",
    );
    root.style.setProperty(
      "--graylightbackground-alpha2",
      "rgba(108, 115, 122, 0.075)",
    );
    root.style.setProperty(
      "--graylightbackground-alpha3",
      "rgba(108, 115, 122, 0.6)",
    );
    root.style.setProperty(
      "--graylightbackground-alpha4",
      "rgba(108, 115, 122, 0.30)",
    );
    root.style.setProperty("--graylightbackground2", "rgba(123, 128, 134, 1)");
    root.style.setProperty("--lighterbackground", "rgb(60, 63, 68)");
    root.style.setProperty("--lightBackgroundFixed", "rgb(45, 48, 52)");
    root.style.setProperty("--lightbackground", "rgb(45, 48, 52)");
    root.style.setProperty("--lightbackground2", "rgba(90, 95, 100, 1)");
    root.style.setProperty("--lightbackground3", "rgb(30, 33, 37)");
    root.style.setProperty("--lightbackground4", "rgb(43, 46, 50)");
    root.style.setProperty("--lightbackground-alpha", "rgba(45, 48, 52, 0.45)");
    root.style.setProperty(
      "--lightbackground-alpha2",
      "rgba(45, 48, 52, 0.025)",
    );
    root.style.setProperty("--lightbackground-alpha3", "rgba(45, 48, 52, 0.5)");
    root.style.setProperty("--background", "rgba(15, 18, 22, 1)");
    root.style.setProperty(
      "--background-alpha-fixed",
      "rgba(45, 48, 52, 0.75)",
    );
    root.style.setProperty("--background-alpha", "rgba(45, 48, 52, 0.75)");
    root.style.setProperty("--background-alpha2", "rgba(15, 18, 22, 0.75)");
    root.style.setProperty("--darkbackgroundFixed", "rgb(0, 2, 5)");
    root.style.setProperty("--darkbackground", "rgb(0, 2, 5)");
    root.style.setProperty("--darkbackground2", "rgba(15, 18, 22, 1)");
    root.style.setProperty("--darkbackground3", "rgba(10, 12, 15, 1)");
    root.style.setProperty("--darkbackground4", "rgba(0, 2, 5, 1)");
    root.style.setProperty(
      "--darkbackground-alpha",
      "rgba(123, 128, 134, 0.25)",
    );
    root.style.setProperty("--darkbackground-alpha2", "rgba(0, 1, 3, 0.15)");
    root.style.setProperty("--darkbackground-alpha3", "rgba(0, 1, 3, 0.30)");
    root.style.setProperty("--text-dark-static", "rgba(71, 75, 80, 1)");
    root.style.setProperty("--text-dark-active", "rgba(0, 0, 0, 1)");
    root.style.setProperty("--text-dark-inactive", "rgb(43, 45, 48)");
    root.style.setProperty("--textactive", "rgba(255, 255, 255, 1)");
    root.style.setProperty("--textactive-2", "rgba(255, 255, 255, 1)");
    root.style.setProperty("--textactive-alpha", "rgba(255, 255, 255, 0.15)");
    root.style.setProperty("--textactive-alpha-2", "rgba(255, 255, 255, 0.40)");
    root.style.setProperty("--textactive-alpha-3", "rgba(255, 255, 255, 0.6)");
    root.style.setProperty("--textInactiveFixed", "rgba(172, 175, 180, 1)");
    root.style.setProperty("--textInactiveFixedWhiteBg", "rgba(95, 95, 95)");
    root.style.setProperty("--textinactive", "rgba(172, 175, 180, 1)");
    root.style.setProperty("--textinactive-2", "rgb(165, 168, 172)");
    root.style.setProperty("--textinactive-alpha", "rgba(145, 150, 155, 0.85)");
    root.style.setProperty(
      "--textinactive-alpha-1",
      "rgba(145, 150, 155, 0.2)",
    );
    root.style.setProperty(
      "--textinactive-alpha-2",
      "rgba(145, 150, 155, 0.15)",
    );
    root.style.setProperty(
      "--textinactive-alpha-3",
      "rgba(145, 150, 155, 0.12)",
    );
    root.style.setProperty("--textstatic", "rgba(142, 146, 150, 1)");
    root.style.setProperty("--textstatic-alpha", "rgba(142, 146, 150, 0.6)");
    root.style.setProperty("--dn-candles", "rgba(123, 128, 134, 1)");
    root.style.setProperty("--dn-candles-alpha", "rgba(123, 128, 134, 0.5)");
    root.style.setProperty("--dn-candles-alpha-2", "rgba(123, 128, 134, 0.1)");
    root.style.setProperty("--navbar-border", "rgb(43, 46, 50)");
    /* Datatable Backgrounds */
    root.style.setProperty("--backgroundTable", "rgba(15, 18, 22, 1)");
    root.style.setProperty("--darkbackgroundTable", "rgb(0, 2, 5)");
    root.style.setProperty("--lightbackgroundTable", "rgb(45, 48, 52)");
    root.style.setProperty("--lightbackground3Table", "rgb(30, 33, 37)");
  } else if (!colorTheme) {
    root.style.setProperty("--symmetry-h", "rgb(10, 100, 242)");
    root.style.setProperty("--symmetry", "rgb(10, 100, 242)");
    root.style.setProperty("--symmetry-alpha", "rgba(10, 100, 242, 0.75)");
    root.style.setProperty("--symmetry-alpha-2", "rgba(0, 88, 208, 0.75)");
    root.style.setProperty("--logo-blue", "rgb(0, 88, 208)");
    root.style.setProperty("--expo", "rgb(19, 69, 242)");
    root.style.setProperty("--expo-alpha", "rgba(19, 69, 242, 0.75)");
    root.style.setProperty("--expo-alpha-2", "rgba(19, 69, 242, 0.1)");
    root.style.setProperty("--expo-alpha-3", "rgba(19, 69, 242, 0.20)");
    root.style.setProperty("--expo-alpha-4", "rgba(19, 69, 242, 0.75)");
    root.style.setProperty("--dark-expo", "rgb(15, 56, 198)");
    root.style.setProperty("--dark-expo-2", "rgb(71, 100, 208)");
    root.style.setProperty("--dark-expo-alpha", "rgba(15, 56, 198, 0.75)");
    root.style.setProperty("--hedge-h", "rgb(26, 159, 222)");
    root.style.setProperty("--hedge", "rgb(0, 202, 226)");
    root.style.setProperty("--hedge-alpha", "rgba(0, 202, 226, 0.75)");
    root.style.setProperty("--hedge-alpha2", "rgba(0, 202, 226, 0.85)");
    root.style.setProperty("--hedge-alpha-3", "rgba(0, 202, 226, 0.2)");
    root.style.setProperty("--dark-hedge", "rgb(0, 144, 161)");
    root.style.setProperty("--dark-hedge-alpha", "rgba(0, 144, 161, 0.60)");
    root.style.setProperty("--net", "rgb(255, 121, 0)");
    root.style.setProperty("--net-alpha", "rgba(255, 121, 0, 0.6)");
    root.style.setProperty("--net_2", "rgb(255, 224, 0)");
    root.style.setProperty("--net-alpha_2", "rgba(255, 224, 0, 0.65)");
    root.style.setProperty("--net-alpha-2", "rgba(255, 121, 0, 0.1)");
    root.style.setProperty("--program", "rgb(0, 99, 99)");
    root.style.setProperty("--program-alpha", "rgba(0, 99, 99, 0.2)");
    root.style.setProperty("--fwd-h", "rgb(10, 231, 161)");
    root.style.setProperty("--fwd", "rgb(9, 204, 143)");
    root.style.setProperty("--fwd-alpha", "rgba(9, 204, 143, 0.60)");
    root.style.setProperty("--fwd-alpha-2", "rgba(9, 204, 143, 0.20)");
    root.style.setProperty("--dark-fwd", "rgb(11, 150, 96)");
    root.style.setProperty("--dark-fwd-alpha", "rgba(11, 150, 96, 0.60)");
    root.style.setProperty("--dark-fwd-alpha-2", "rgba(11, 150, 96, 0.20)");
    root.style.setProperty("--call", "rgb(249, 49, 159)");
    root.style.setProperty("--call-alpha", "rgba(249, 49, 142, 0.60)");
    root.style.setProperty("--call-alpha-2", "rgba(249, 49, 142, 0.20)");
    root.style.setProperty("--dark-call", "rgb(224, 12, 59)");
    root.style.setProperty("--dark-call-alpha", "rgba(224, 12, 59, 0.60)");
    root.style.setProperty("--put", "rgb(105, 105, 255)");
    root.style.setProperty("--put-alpha", "rgba(105, 105, 255, 0.2)");
    root.style.setProperty("--dark-put", "rgb(74, 63, 229)");
    root.style.setProperty("--dark-put-alpha", "rgba(74, 63, 229, 0.2)");
    root.style.setProperty("--pink", "rgb(249, 49, 197)");
    root.style.setProperty("--dark-pink", "rgb(232, 35, 113)");
    root.style.setProperty("--red", "rgb(255, 0, 59)");
    root.style.setProperty("--red-alpha", "rgba(255, 0, 59, 0.60)");
    root.style.setProperty("--red-alpha-2", "rgba(255, 0, 59, 0.10)");
    root.style.setProperty("--red-alpha-3", "rgba(255, 0, 59, 0.20)");
    root.style.setProperty("--dark-red", "rgb(171, 1, 34)");
    root.style.setProperty("--dark-red-alpha", "rgba(171, 1, 34, 0.60)");
    root.style.setProperty("--darker-red", "rgb(122, 0, 17)");
    root.style.setProperty("--green", "rgb(9, 204, 143)");
    root.style.setProperty("--dark-green", "rgb(11, 150, 96)");
    root.style.setProperty("--whiteGray", "rgb(75, 75, 75)");
    root.style.setProperty("--whiteGray-alpha", "rgba(75, 75, 75, 0.60)");
    root.style.setProperty("--graylightbackground", "rgba(170, 170, 173, 1)");
    root.style.setProperty(
      "--graylightbackground-alpha",
      "rgba(200, 200, 203, 0.5)",
    );
    root.style.setProperty(
      "--graylightbackground-alpha2",
      "rgba(200, 200, 203, 0.075)",
    );
    root.style.setProperty(
      "--graylightbackground-alpha3",
      "rgba(200, 200, 203, 0.6)",
    );
    root.style.setProperty(
      "--graylightbackground-alpha4",
      "rgba(200, 200, 203, 0.30)",
    );
    root.style.setProperty("--graylightbackground2", "rgba(150, 150, 153, 1)");
    root.style.setProperty("--lighterbackground", "rgb(215, 215, 218)");
    root.style.setProperty("--lightbackground", "rgb(230, 230, 233)");
    root.style.setProperty("--lightbackground2", "rgba(200, 200, 203, 1)");
    root.style.setProperty("--lightbackground3", "rgb(245, 245, 248)");
    root.style.setProperty("--lightbackground4", "rgb(180, 180, 183)");
    root.style.setProperty(
      "--lightbackground-alpha",
      "rgba(213, 213, 216, 0.45)",
    );
    root.style.setProperty(
      "--lightbackground-alpha2",
      "rgba(213, 213, 216, 0.025)",
    );
    root.style.setProperty(
      "--lightbackground-alpha3",
      "rgba(213, 213, 216, 0.5)",
    );
    root.style.setProperty("--background", "rgba(240, 240, 243, 1)");
    root.style.setProperty("--background-alpha", "rgba(240, 240, 243, 0.75)");
    root.style.setProperty("--background-alpha2", "rgba(240, 240, 243, 0.75)");
    root.style.setProperty("--darkbackground", "rgb(252, 252, 255)");
    root.style.setProperty("--darkbackground2", "rgba(235, 235, 238, 1)");
    root.style.setProperty("--darkbackground3", "rgba(240, 240, 243, 1)");
    root.style.setProperty("--darkbackground4", "rgba(225, 225, 228, 1)");
    root.style.setProperty(
      "--darkbackground-alpha",
      "rgba(119, 119, 122, 0.25)",
    );
    root.style.setProperty(
      "--darkbackground-alpha2",
      "rgba(252, 252, 255, 0.15)",
    );
    root.style.setProperty(
      "--darkbackground-alpha3",
      "rgba(252, 252, 252, 0.30)",
    );
    root.style.setProperty("--text-dark-static", "rgba(175, 175, 175, 1)");
    root.style.setProperty("--text-dark-active", "rgba(255, 255, 255, 1)");
    root.style.setProperty("--text-dark-inactive", "rgb(215, 205, 194)");
    root.style.setProperty("--textactive", "rgba(0, 0, 0, 1)");
    root.style.setProperty("--textactive-2", "rgba(255, 255, 255, 1)");
    root.style.setProperty("--textactive-alpha", "rgba(0, 0, 0, 0.15)");
    root.style.setProperty("--textactive-alpha-2", "rgba(0, 0, 0, 0.40)");
    root.style.setProperty("--textinactive", "rgba(95, 95, 95)");
    root.style.setProperty("--textinactive-alpha", "rgba(95, 95, 95, 0.5)");
    root.style.setProperty("--textinactive-alpha-1", "rgba(95, 95, 95, 0.2)");
    root.style.setProperty("--textinactive-alpha-2", "rgba(95, 95, 95, 0.15)");
    root.style.setProperty("--textinactive-alpha-3", "rgba(95, 95, 95, 0.12)");
    root.style.setProperty("--textstatic", "rgba(103, 103, 103, 1)");
    root.style.setProperty("--textstatic-alpha", "rgba(151, 148, 144, 0.9)");
    root.style.setProperty("--dn-candles", "rgba(111, 111, 111, 1)");
    root.style.setProperty("--dn-candles-alpha", "rgba(111, 111, 111, 0.5)");
    root.style.setProperty("--dn-candles-alpha-2", "rgba(111, 111, 111, 0.1)");
    root.style.setProperty("--navbar-border", "rgba(200, 200, 200, 1)");
    /* Datatable Backgrounds */
    root.style.setProperty("--backgroundTable", "rgb(252, 252, 255)");
    root.style.setProperty("--darkbackgroundTable", "rgba(210, 210, 213, 1)");
    root.style.setProperty("--lightbackgroundTable", "rgb(220, 220, 223)");
    root.style.setProperty("--lightbackground3Table", "rgb(235, 235, 238)");
  }
}
